import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import type {
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
} from '@/types/user.types';
import type {
  CreateRoomRequest,
  CreateRoomResponse,
  JoinRoomRequest,
  JoinRoomResponse,
  GetRoomResponse,
} from '@/types/room.types';

// Criar instância do axios
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor de request para adicionar token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de response para refresh automático em 401
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = useAuthStore.getState().refreshToken;
        if (!refreshToken) {
          useAuthStore.getState().logout();
          throw error;
        }

        const { data } = await axios.post<RefreshTokenResponse>(
          `${api.defaults.baseURL}/api/auth/refresh`,
          { refreshToken }
        );

        useAuthStore.getState().updateAccessToken(data.accessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        }

        return api(originalRequest);
      } catch (refreshError) {
        useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// === AUTH API ===

export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const { data } = await api.post<LoginResponse>('/api/auth/login', credentials);
    return data;
  },

  logout: async (refreshToken: string): Promise<void> => {
    await api.post('/api/auth/logout', { refreshToken });
  },
};

// === ROOMS API ===

export const roomsApi = {
  createRoom: async (templateId: number): Promise<CreateRoomResponse> => {
    const payload: CreateRoomRequest = { template_id: templateId };
    const { data } = await api.post<CreateRoomResponse>('/api/rooms/create', payload);
    return data;
  },

  joinRoom: async (code: string): Promise<JoinRoomResponse> => {
    const payload: JoinRoomRequest = { code };
    const { data } = await api.post<JoinRoomResponse>('/api/rooms/join', payload);
    return data;
  },

  getRoom: async (code: string): Promise<GetRoomResponse> => {
    const { data } = await api.get<GetRoomResponse>(`/api/rooms/${code}`);
    return data;
  },
};

// === CONVERSATIONS API ===

export interface ConversationMessage {
  id: number;
  messageType: 'chat' | 'transcription';
  senderName: string;
  text: string;
  createdAt: string;
}

export interface ConversationHistoryResponse {
  roomCode: string;
  messages: ConversationMessage[];
  total: number;
}

export interface SaveMessageRequest {
  message_type: 'chat' | 'transcription';
  sender_name: string;
  text: string;
}

export type MessageTypeFilter = 'chat' | 'transcription' | undefined;

export const conversationsApi = {
  /**
   * Get conversation history with optional filtering by message type.
   * Access control is enforced by backend based on user role.
   */
  getHistory: async (
    roomCode: string,
    messageType?: MessageTypeFilter
  ): Promise<ConversationHistoryResponse> => {
    const params = messageType ? { message_type: messageType } : {};
    const { data } = await api.get<ConversationHistoryResponse>(
      `/api/conversations/${roomCode}`,
      { params }
    );
    return data;
  },

  /**
   * Save a single message to the conversation.
   * Transcription messages can only be saved by room owner (admin/analyst).
   */
  saveMessage: async (roomCode: string, message: SaveMessageRequest): Promise<ConversationMessage> => {
    const { data } = await api.post<ConversationMessage>(
      `/api/conversations/${roomCode}`,
      message
    );
    return data;
  },
};

export default api;
